import { isBoolean, isNil, isNumber } from 'lodash';
import { CellData, HotComputedCellValue } from '../../../../../../common/table/TableTypes';
import { CellTypeConstants, CellTypeNames, TextAlignment } from '../../../../../../common/table/CellTypeConstants';
import { TABLE_CELL_DEFAULT_TEXT_ALIGNMENT } from '../../../../../../common/table/tableConstants';
import { shouldUpdateCellType } from '../../../../../../common/table/utils/tableCellTypeUtils';
import { isCellTypeNumeric } from '../../../../../../common/table/utils/tableCellDataPropertyUtils';
import { getRawTextFromCellContentString } from '../../../../../../common/table/utils/tableCellContentStringUtils';
import { stringShouldFormatAsType } from '../../../../../../common/table/utils/tableInputGeneralUtils';
import { getIsInputNumber } from '../../../../../../common/table/utils/tableInputNumberUtils';
import { getCurrencyFromString } from '../../../../../../common/table/utils/tableInputCurrencyUtils';
import { getLocalNumberFormattingSymbols } from '../../../../../../common/utils/localLanguageUtils';
import { getCellRenderValue } from './tableCellRenderValueUtils';

/**
 * Converts stored numeric content into the locale-aware value shown while editing.
 */
export const getLocalisedContent = (cellData: CellData, locale: string): string | number => {
    if (cellData.value === null) return '';
    if (!isCellTypeNumeric(cellData.type?.name)) return cellData.value;

    const { decimalSeparator } = getLocalNumberFormattingSymbols(locale);
    return cellData.value.replaceAll('.', decimalSeparator);
};

/**
 * Returns the alignment the renderer should use for a cell.
 */
export const getCellTextAlignment = (
    cellData: CellData,
    locale: string,
    hotComputedCellValue?: HotComputedCellValue,
): TextAlignment => {
    if (!cellData) return TABLE_CELL_DEFAULT_TEXT_ALIGNMENT;
    if (cellData.textAlignment) return cellData.textAlignment;

    const cellTypeName = cellData.type?.name || CellTypeNames.AUTO;
    const cellTypeDefaultAlignment =
        (CellTypeConstants[cellTypeName].alignment as TextAlignment) || TABLE_CELL_DEFAULT_TEXT_ALIGNMENT;

    if (!cellData.value || cellTypeName === CellTypeNames.TEXT) return cellTypeDefaultAlignment;

    if (!isNil(hotComputedCellValue) && isNumber(hotComputedCellValue)) return TextAlignment.RIGHT;
    if (!isNil(hotComputedCellValue) && isBoolean(hotComputedCellValue)) return TextAlignment.LEFT;

    const text = getRawTextFromCellContentString(cellData.value);
    const { newCellType } = stringShouldFormatAsType(String(text), false, locale) || {};

    if (
        newCellType &&
        (!cellData.type || shouldUpdateCellType(newCellType, cellData.type.name) || newCellType === cellData.type.name)
    ) {
        return CellTypeConstants[newCellType].alignment as TextAlignment;
    }

    const displayValue = !isNil(hotComputedCellValue)
        ? getCellRenderValue(cellData, locale, hotComputedCellValue)
        : text;

    if (isNaN(Number(displayValue))) return TextAlignment.LEFT;

    return cellTypeDefaultAlignment;
};

/**
 * Returns the editor alignment override while the user types.
 */
export const getCellEditingAlignment = (
    editCellValue: string,
    cellData: CellData,
    locale: string,
): TextAlignment | null => {
    if (editCellValue.length > 5) return null;

    const { currencySymbol } = getCurrencyFromString(editCellValue);
    const symbolIsNotLetter = currencySymbol && !currencySymbol?.match(/^[a-zA-Z]+$/);
    if (symbolIsNotLetter) return TextAlignment.RIGHT;

    const isCurrencyValue = currencySymbol && getIsInputNumber(editCellValue, true, locale);
    const shouldUpdateAlignment = editCellValue.length === 1 || isCurrencyValue;
    if (!shouldUpdateAlignment) return null;

    return getCellTextAlignment({ ...cellData, value: editCellValue }, locale);
};
